/**
 * MeineDatenPage — Betroffenenrechte (DSGVO Art. 15/17/20) Self-Service.
 * - Datenexport (JSON) herunterladen
 * - Löschung/Anonymisierung beantragen
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function MeineDatenPage() {
    const [exporting, setExporting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [requested, setRequested] = useState(false);

    const doExport = async () => {
        setExporting(true);
        try {
            const { data } = await api.get("/privacy/my-data");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `meine-daten-${new Date().toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            toast.success("Datenexport heruntergeladen.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Export fehlgeschlagen. Bitte einloggen.");
        } finally { setExporting(false); }
    };

    const doDelete = async () => {
        if (!window.confirm("Möchten Sie die Löschung Ihrer personenbezogenen Daten beantragen? Gesetzliche Aufbewahrungspflichten (z. B. für Rechnungen) bleiben unberührt.")) return;
        setDeleting(true);
        try {
            const { data } = await api.post("/privacy/delete-request");
            setRequested(true);
            toast.success(data.status === "already_requested" ? "Antrag liegt bereits vor." : "Löschantrag eingegangen.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Antrag fehlgeschlagen. Bitte einloggen.");
        } finally { setDeleting(false); }
    };

    return (
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-10" data-testid="privacy-rights-page">
            <Link to={-1} onClick={(e) => { e.preventDefault(); window.history.back(); }} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#111827] mb-4">
                <ArrowLeft className="w-4 h-4" /> Zurück
            </Link>
            <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="w-7 h-7 text-[#FFCC00]" />
                <h1 className="font-display text-3xl font-black tracking-tight">Meine Daten &amp; Rechte</h1>
            </div>
            <p className="text-sm text-gray-500 mb-8">Ihre Rechte nach Art. 15, 17 und 20 DSGVO.</p>

            <div className="space-y-4">
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                    <h2 className="font-bold text-gray-900">Datenauskunft &amp; Export (Art. 15/20)</h2>
                    <p className="mt-1 text-sm text-gray-600">Laden Sie eine maschinenlesbare Kopie Ihrer Account-Stammdaten herunter (JSON).</p>
                    <button onClick={doExport} disabled={exporting} className="mt-3 inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white hover:bg-black disabled:opacity-60" data-testid="privacy-export-btn">
                        {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Meine Daten exportieren
                    </button>
                </div>

                <div className="rounded-xl border border-red-200 bg-red-50/40 p-5">
                    <h2 className="font-bold text-gray-900">Löschung beantragen (Art. 17)</h2>
                    <p className="mt-1 text-sm text-gray-600">Beantragen Sie die Löschung/Anonymisierung Ihrer personenbezogenen Daten. Daten, die gesetzlichen Aufbewahrungspflichten unterliegen (z. B. Rechnungen, §147 AO), werden erst nach Ablauf der Fristen gelöscht.</p>
                    <button onClick={doDelete} disabled={deleting || requested} className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-60" data-testid="privacy-delete-btn">
                        {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {requested ? "Antrag eingegangen" : "Löschung beantragen"}
                    </button>
                </div>
            </div>
        </div>
    );
}
